if (!customElements.get('product-card-slider')) {
  customElements.define('product-card-slider', class ProductCardSlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.bar = null;
      this.prevButton = null;
      this.nextButton = null;
      this.pointerDown = false;
      this.pointerMoved = false;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.dragThreshold = 8;
      this.userInteracted = false;
      this.hoverQuery = window.matchMedia('(hover: hover) and (min-width: 990px)');
    }

    connectedCallback() {
      // Elements are now siblings in the parent container
      const container =
        this.closest('.reco-img-wrap') ||
        this.closest('.card-qude__media-container') ||
        this.closest('.card-qude__link') ||
        this.parentElement;
      if (container) {
        this.bar = container.querySelector('.card-slider-bar__inner');
        this.hint = container.querySelector('.card-qude__drag-hint');
        this.prevButton = container.querySelector('[data-slider-prev]');
        this.nextButton = container.querySelector('[data-slider-next]');

        container.addEventListener('mouseenter', () => {
          if (this.dataset.hoverSecondImage !== 'true') return;
          if (!this.hoverQuery.matches || !this.slider || this.pointerDown) return;
          this.userInteracted = false;
          if (this.slider.track.details.slides.length > 1) {
            this.slider.moveToIdx(1, true);
          }
        });

        container.addEventListener('mouseleave', () => {
          if (this.dataset.hoverSecondImage !== 'true') return;
          if (!this.hoverQuery.matches || !this.slider || this.pointerDown) return;
          if (this.userInteracted) return;
          this.slider.moveToIdx(0, true);
        });
      }

      this.addEventListener(
        'click',
        (e) => {
          if (!this.pointerMoved) return;
          e.preventDefault();
          e.stopPropagation();
          this.pointerMoved = false;
        },
        true
      );

      this.addEventListener(
        'pointerdown',
        (e) => {
          if (e.button !== undefined && e.button !== 0) return;
          this.pointerDown = true;
          this.pointerMoved = false;
          this.pointerStartX = e.clientX || 0;
          this.pointerStartY = e.clientY || 0;
        },
        { passive: true }
      );

      this.addEventListener(
        'pointermove',
        (e) => {
          if (!this.pointerDown || this.pointerMoved) return;
          const dx = Math.abs((e.clientX || 0) - this.pointerStartX);
          const dy = Math.abs((e.clientY || 0) - this.pointerStartY);
          if (dx + dy >= this.dragThreshold) {
            this.pointerMoved = true;
            this.hideHint();
          }
        },
        { passive: true }
      );

      const endPointer = () => {
        this.pointerDown = false;
      };
      this.addEventListener('pointerup', endPointer, { passive: true });
      this.addEventListener('pointercancel', endPointer, { passive: true });

      this.initSlider();
    }

    hideHint() {
      // Simply remove the class that makes it visible
      this.classList.remove('show-hint');
      if (this.hint) this.hint.classList.remove('show-hint');
    }

    initSlider() {
      if (typeof KeenSlider === 'undefined') {
        setTimeout(() => this.initSlider(), 100);
        return;
      }

      if (this.slider) return;

      const enableDesktop = this.dataset.enableDesktop === 'true' || this.hasAttribute('data-enable-desktop');
      const breakpoints = enableDesktop
        ? undefined
        : {
            '(min-width: 990px)': {
              disabled: true,
            },
          };

      this.slider = new KeenSlider(this, {
        selector: '.keen-slider__slide',
        loop: false,
        slides: {
          perView: 1,
          spacing: 0,
        },
        dragSpeed: 1,
        initial: 0,
        breakpoints,
        defaultAnimation: {
          duration: 0,
        },
        dragStarted: () => {
          this.userInteracted = true;
          this.hideHint();
        },
        created: (s) => {
          this.classList.add('slider-initialized');
          this.updateBar(s);

          if (this.prevButton && !this.prevButton.dataset.bound) {
            this.prevButton.dataset.bound = 'true';
            this.prevButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.userInteracted = true;
              const rel = s.track?.details?.rel ?? 0;
              s.moveToIdx(Math.max(0, rel - 1), true, { duration: 0 });
            });
          }

          if (this.nextButton && !this.nextButton.dataset.bound) {
            this.nextButton.dataset.bound = 'true';
            this.nextButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.userInteracted = true;
              const details = s.track?.details;
              const rel = details?.rel ?? 0;
              const max = Math.max(0, (details?.slides?.length ?? 1) - 1);
              s.moveToIdx(Math.min(max, rel + 1), true, { duration: 0 });
            });
          }
          
          // Show hint if more than 1 slide and hasn't been shown yet
          if (s.track.details.slides.length > 1 && !window.hasShownSwipeHint) {
            this.classList.add('show-hint');
            if (this.hint) this.hint.classList.add('show-hint');
            window.hasShownSwipeHint = true;
          }
        },
        detailsChanged: (s) => {
          this.updateBar(s);
        },
        slideChanged: (s) => {
           this.updateBar(s);
        }
      });
    }

    updateBar(s) {
      // If slider is disabled (desktop), do nothing or hide bar
      if (s.options.disabled) return;

      if (!this.bar || !s.track.details) return;
      
      const slides = s.track.details.slides.length;
      
      // Safety check if bar exists
      if (this.bar.parentElement) {
          if (slides <= 1) {
             this.bar.parentElement.style.display = 'none';
             return;
          } else {
             this.bar.parentElement.style.display = 'block';
          }
      }

      // Set width
      const width = 100 / slides;
      this.bar.style.width = `${width}%`;

      // Set position
      // Use progress for smooth movement
      const progress = Math.max(0, Math.min(1, s.track.details.progress));
      const slideIndex = progress * (slides - 1);
      this.bar.style.transform = `translateX(${slideIndex * 100}%)`;
    }

    disconnectedCallback() {
      if (this.slider) this.slider.destroy();
    }
  });
}
